/**
 * Spatial Repeatability Filter: which patches reproduce reliably.
 *
 * Shows the measured palette both in CIELab and laid out on the marked grid.
 * Patches whose repetitions differ by more than the acceptable dE00 are
 * excluded automatically; the operator can exclude further ones by eye. The
 * repetition-drift view compares repetition 1 with repetition 2 per capture
 * mode, which is how a drifting setup is told apart from a noisy patch.
 *
 * Confirm applies the exclusions to the palette - a one-way step.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Button, Typography, Paper, CircularProgress, Box, Alert, Chip,
    FormControlLabel, Switch, TextField, ToggleButton, ToggleButtonGroup,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ReferenceLine } from "recharts";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LoadingComponent from "../../components/LoadingComponent";
import RepeatabilityGamutPlot from "../../components/RepeatabilityGamutPlot";
import MeasurementDriftPlot from "../../components/MeasurementDriftPlot";
import RepeatabilityGrid from "../../components/RepeatabilityGrid";
import { modeLabel } from "../../helper/repeatabilityText";
import { repetitionDriftSummary, signedDifference } from "../../helper/repetitionDrift";
import {
    selectionSidebar, selectionLockedRoute, isModeContrastSelection,
    isImageSelection,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

const cardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    m: 0,
    boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
};

// Step 6: the spatial repeatability filter — the measured gamut
// in 3D and the marked grid on which further patches can be excluded manually.
// Nothing is removed from the palette until "Confirm" (the Next button),
// which finishes the selection.
export default function ColorSelectionRepeatabilityFilter() {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    /** api/selection/spatial/results payload. */
    const [repeatability, setRepeatability] = useState(null);
    const [paintMode, setPaintMode] = useState(1);
    const [showRejected, setShowRejected] = useState(false);
    /** Capture mode shown in the drift plot. */
    const [driftMode, setDriftMode] = useState(1);
    /** Capture mode shown in the repetition drift plot. */
    const [repetitionMode, setRepetitionMode] = useState(1);
    const [savingRejection, setSavingRejection] = useState(false);
    /** Acceptable ΔE00 per mode as typed; applied live (debounced). */
    const [limitInputs, setLimitInputs] = useState([]);
    const [applyingLimits, setApplyingLimits] = useState(false);
    const limitTimer = useRef(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const navigate = useNavigate();

    const { selectionId, selectionType: urlType, stepRoute } = useSelectionParams();
    const [selectionType, setSelectionType] = useState(urlType);
    // A mode contrast selection continues with the Mode Contrast Filter after Confirm.
    const modeContrastType = isModeContrastSelection(selectionType);
    // An image selection continues with its width groups.
    const imageType = isImageSelection(selectionType);
    const continues = modeContrastType || imageType;
    const nextStepName = modeContrastType ? "the Mode Contrast Filter" : "the width groups";
    const nextRoute = stepRoute(
        modeContrastType ? "modecontrast" : imageType ? "widthgroups" : "overview",
        selectionType,
    );

    useEffect(() => {
        const load = async () => {
            try {
                const selectionResponse = await axios.get('api/color/selection/' + selectionId + "/");
                const locked = selectionLockedRoute(
                    selectionId, selectionResponse.data.status,
                    selectionResponse.data.selection_type || selectionType,
                );
                if (locked) {
                    navigate(locked, { replace: true });
                    return;
                }
                if (selectionResponse.data.selection_type) setSelectionType(selectionResponse.data.selection_type);
                const response = await axios.post('api/selection/spatial/results', { selectionId });
                setRepeatability(response.data.repeatability || null);
                if (response.data.repeatability) {
                    setLimitInputs(response.data.repeatability.limits.map(String));
                }
            } catch (error) {
                console.error('Error loading the repeatability result:', error);
                setErrorMessage("Loading the repeatability result failed.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // By-eye rejection: toggling a patch on the grid is saved right away.
    const toggleRejection = async (sampleId) => {
        if (!repeatability || savingRejection) return;
        const current = new Set(repeatability.manual_rejected || []);
        if (current.has(sampleId)) current.delete(sampleId); else current.add(sampleId);
        setSavingRejection(true);
        try {
            const response = await axios.post('api/selection/spatial/rejection', {
                selectionId, rejectedSamples: Array.from(current),
            });
            setRepeatability(response.data.repeatability || null);
        } catch (error) {
            console.error('Error saving rejection:', error);
            setErrorMessage(error?.response?.data?.error || "Saving the rejection failed.");
        } finally {
            setSavingRejection(false);
        }
    };

    // The acceptable ΔE00 per mode: the verdicts are recomputed from the
    // stored measurement, nothing is re-measured.
    const applyLimits = async (values) => {
        setApplyingLimits(true);
        setErrorMessage(null);
        try {
            const response = await axios.post('api/selection/spatial/settings', {
                selectionId, limits: values,
            });
            if (response.data.repeatability) setRepeatability(response.data.repeatability);
        } catch (error) {
            console.error('Error applying the limits:', error);
            setErrorMessage(error?.response?.data?.error || "Applying the acceptable ΔE00 failed.");
        } finally {
            setApplyingLimits(false);
        }
    };

    // Typing a new limit re-evaluates the measurement after a short pause.
    const changeLimit = (index, value) => {
        const next = limitInputs.map((v, j) => (j === index ? value : v));
        setLimitInputs(next);
        const numbers = next.map(Number);
        clearTimeout(limitTimer.current);
        if (!numbers.every((v) => Number.isFinite(v) && v > 0)) return;
        limitTimer.current = setTimeout(() => applyLimits(numbers), 400);
    };

    const confirmRepeatability = async () => {
        setConfirming(true);
        setErrorMessage(null);
        try {
            await axios.post('api/selection/spatial/confirm', { selectionId });
            setConfirmDialogOpen(false);
            navigate(nextRoute);
        } catch (error) {
            console.error('Error confirming repeatability:', error);
            setConfirmDialogOpen(false);
            setErrorMessage(error?.response?.data?.error || "Confirming the repeatability result failed.");
        } finally {
            setConfirming(false);
        }
    };

    const counts = repeatability?.counts;

    // dE00 between the repetitions, per mode, binned for the histogram; the
    // acceptable limit is drawn as a reference line.
    const histograms = repeatability ? Array.from({ length: repeatability.modes }, (_, i) => {
        const mode = String(i + 1);
        const values = repeatability.cells.map((c) => c.modes[mode]?.deltaE).filter((v) => v != null);
        if (!values.length) return null;
        const limit = repeatability.limits[i];
        const maxValue = Math.max(...values, limit, 1);
        const binCount = 12;
        const binWidth = maxValue / binCount;
        const bins = Array.from({ length: binCount }, (_, k) => ({
            bin: `${(k * binWidth).toFixed(1)}`,
            from: k * binWidth,
            count: values.filter((v) => v >= k * binWidth && (v < (k + 1) * binWidth || (k === binCount - 1 && v <= maxValue))).length,
        }));
        return { mode: i + 1, bins, limit, binWidth };
    }).filter(Boolean) : [];

    // Repetition 2 − repetition 1 per mode, over the patches the plots show.
    const repetitionDrift = repeatability
        ? repetitionDriftSummary(repeatability.cells, repeatability.modes, showRejected) : [];

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Spatial Repeatability Filter"
                    subtitle="The measured palette in CIELab and on the marked grid. Patches whose repetitions differ by more than the acceptable ΔE00 are excluded automatically; further patches can be excluded manually. Confirm applies the filter to the palette."
                    noPaper
                    stepSidebar={selectionSidebar("review", counts ? counts.kept : null, selectionType)}
                    contentBoxSx={{
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        px: 0,
                        py: { xs: 2, md: 3 },
                    }}
                >
                    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1, alignItems: "stretch" }}>
                        {errorMessage && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage}</Alert>}

                        {loading ? (
                            <LoadingComponent />
                        ) : !repeatability ? (
                            <Alert
                                severity="info" sx={{ borderRadius: 2 }}
                                action={
                                    <Button size="small" onClick={() => navigate(stepRoute("spatial", selectionType))} sx={{ textTransform: "none" }}>
                                        Back to the measurement
                                    </Button>
                                }
                            >
                                The repeatability has not been measured yet.
                            </Alert>
                        ) : (
                            <>
                                <Paper elevation={0} sx={cardSx}>
                                    <Stack spacing={2.5} sx={{ width: "100%" }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>Acceptable ΔE00</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                A patch is excluded when the ΔE00 between its repetitions reaches
                                                this value{repeatability.modes > 1 ? " in either capture mode" : ""}.
                                                The verdicts below update as the value is changed; the measurement
                                                itself is not repeated.
                                            </Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                                {limitInputs.map((value, i) => (
                                                    <TextField
                                                        key={i}
                                                        size="small"
                                                        type="number"
                                                        label={repeatability.modes > 1 ? `ΔE00 — ${modeLabel(i + 1)}` : "ΔE00"}
                                                        value={value}
                                                        onChange={(e) => changeLimit(i, e.target.value)}
                                                        inputProps={{ min: 0.1, step: 0.5, "aria-label": `Acceptable dE00 mode ${i + 1}` }}
                                                        error={!(Number(value) > 0)}
                                                        sx={{ width: 170 }}
                                                    />
                                                ))}
                                                {applyingLimits ? <CircularProgress size={18} thickness={5} aria-label="Applying limits" /> : null}
                                            </Stack>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>Result</Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                                <Chip size="small" label={`${counts.total} patches`} />
                                                <Chip size="small" color="success" variant="outlined" label={`${counts.repeatable} within the limit`} />
                                                {repeatability.modes > 1 && Object.entries(counts.repeatable_per_mode).map(([mode, n]) => (
                                                    <Chip key={mode} size="small" variant="outlined" label={`${modeLabel(mode)}: ${n} within ${repeatability.limits[Number(mode) - 1]}`} />
                                                ))}
                                                <Chip size="small" color="error" variant="outlined" label={`${counts.manual_rejected} excluded manually`} />
                                                <Chip size="small" color="primary" label={`${counts.kept} retained`} />
                                            </Stack>
                                        </Box>

                                        {histograms.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                                    ΔE00 between the repetitions
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                    Distribution over all patches; the red line is the acceptable ΔE00 of the mode.
                                                </Typography>
                                                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                                    {histograms.map((h) => (
                                                        <Box key={h.mode}>
                                                            {repeatability.modes > 1 && (
                                                                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" align="center">
                                                                    {modeLabel(h.mode)}
                                                                </Typography>
                                                            )}
                                                            <BarChart width={400} height={260} data={h.bins} margin={{ top: 12, right: 16, left: 12, bottom: 28 }}>
                                                                <XAxis dataKey="bin" label={{ value: "ΔE00", position: "insideBottom", offset: -12 }} />
                                                                <YAxis allowDecimals={false} label={{ value: "Patches", angle: -90, position: "center" }} />
                                                                <CartesianGrid strokeDasharray="3 3" />
                                                                <ChartTooltip />
                                                                <ReferenceLine x={h.bins.find((b) => b.from + h.binWidth > h.limit)?.bin} stroke="#d32f2f" strokeWidth={2} />
                                                                <Bar dataKey="count" fill="#64748b" isAnimationActive={false} />
                                                            </BarChart>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        <Box>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    Measured gamut (CIELab)
                                                </Typography>
                                                <FormControlLabel
                                                    control={<Switch size="small" checked={showRejected} onChange={(e) => setShowRejected(e.target.checked)} />}
                                                    label={<Typography variant="body2">Show excluded patches</Typography>}
                                                />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                {repeatability.modes > 1
                                                    ? "Each retained patch is drawn twice — a sphere at its mode 1 reading, a diamond at its mode 2 reading — connected by a line whose length indicates the colour difference between the two modes. Drag to rotate, scroll to zoom."
                                                    : "Each retained patch is drawn at its measured CIELab coordinates in its own colour. Drag to rotate, scroll to zoom."}
                                            </Typography>
                                            <Box sx={{ width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                                <RepeatabilityGamutPlot
                                                    cells={repeatability.cells}
                                                    modes={repeatability.modes}
                                                    limits={repeatability.limits}
                                                    showRejected={showRejected}
                                                    height={520}
                                                />
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    Repetition drift (repetition 1 → repetition 2)
                                                </Typography>
                                                {repeatability.modes > 1 && (
                                                    <ToggleButtonGroup
                                                        size="small" exclusive value={repetitionMode}
                                                        onChange={(_e, v) => v && setRepetitionMode(v)}
                                                        aria-label="Capture mode shown in the repetition drift plot"
                                                    >
                                                        {Array.from({ length: repeatability.modes }, (_, i) => i + 1).map((mode) => (
                                                            <ToggleButton key={mode} value={mode} sx={{ textTransform: "none", px: 1.5 }}>{modeLabel(mode)}</ToggleButton>
                                                        ))}
                                                    </ToggleButtonGroup>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                Each patch as a line from its reading on repetition 1 (small grey marker) to its reading on repetition 2 (sphere in the measured colour); the length of the line is the ΔE00 between the repetitions. Excluded patches follow the switch above.
                                            </Typography>
                                            {repetitionDrift.length > 0 && (
                                                <Box sx={{ width: "100%", maxWidth: 900, overflowX: "auto", mb: 1.5 }}>
                                                    <Table size="small" aria-label="Repetition drift per mode">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Mode</TableCell>
                                                                <TableCell align="right">Patches</TableCell>
                                                                <TableCell align="right">ΔL*</TableCell>
                                                                <TableCell align="right">Δa*</TableCell>
                                                                <TableCell align="right">Δb*</TableCell>
                                                                <TableCell align="right">Mean ΔE00</TableCell>
                                                                <TableCell align="right">Max ΔE00</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {repetitionDrift.map((row) => (
                                                                <TableRow key={row.mode}>
                                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{modeLabel(row.mode)}</TableCell>
                                                                    <TableCell align="right">{row.count}</TableCell>
                                                                    {[row.dL, row.da, row.db].map((d, i) => (
                                                                        <TableCell key={i} align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                                                            {`${signedDifference(d.mean)} ± ${d.sd.toFixed(2)}`}
                                                                        </TableCell>
                                                                    ))}
                                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{row.deltaE.mean.toFixed(2)}</TableCell>
                                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{row.deltaE.max.toFixed(2)}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                        ΔL*, Δa*, Δb*: repetition 2 − repetition 1, mean ± standard deviation over the patches shown.
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                                <MeasurementDriftPlot
                                                    cells={repeatability.cells}
                                                    limits={repeatability.limits}
                                                    mode={repetitionMode}
                                                    reference="repetition"
                                                    showExcluded={showRejected}
                                                    height={480}
                                                />
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    Measurement drift (exploration value → plate reading)
                                                </Typography>
                                                {repeatability.modes > 1 && (
                                                    <ToggleButtonGroup
                                                        size="small" exclusive value={driftMode}
                                                        onChange={(_e, v) => v && setDriftMode(v)}
                                                        aria-label="Capture mode shown in the drift plot"
                                                    >
                                                        {Array.from({ length: repeatability.modes }, (_, i) => i + 1).map((mode) => (
                                                            <ToggleButton key={mode} value={mode} sx={{ textTransform: "none", px: 1.5 }}>{modeLabel(mode)}</ToggleButton>
                                                        ))}
                                                    </ToggleButtonGroup>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                Each patch as a line from the value it was selected with (the exploration&apos;s measurement, small grey marker) to its reading on the plate (sphere in the measured colour); the length of the line is the drift ΔE00. Excluded patches follow the switch above.
                                            </Typography>
                                            <Box sx={{ width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                                                <MeasurementDriftPlot
                                                    cells={repeatability.cells}
                                                    limits={repeatability.limits}
                                                    mode={driftMode}
                                                    showExcluded={showRejected}
                                                    height={480}
                                                />
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" fontWeight={700}>Marked grid</Typography>
                                                {savingRejection ? <CircularProgress size={16} thickness={5} aria-label="Saving rejection" /> : null}
                                            </Stack>
                                            <RepeatabilityGrid
                                                repeatability={repeatability}
                                                paintMode={paintMode}
                                                onPaintModeChange={setPaintMode}
                                                onToggle={toggleRejection}
                                            />
                                        </Box>

                                        <Typography variant="body2" color="text.secondary">
                                            {continues
                                                ? `Confirm applies the filter: ${counts.kept} of ${counts.total} patches remain in the palette; ${nextStepName} follow${modeContrastType ? "s" : ""}. To replace a photo, return to the measurement page and measure again.`
                                                : `Confirm applies the filter: ${counts.kept} of ${counts.total} patches remain in the palette and the selection is finalized. To replace a photo, return to the measurement page and measure again.`}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </>
                        )}

                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("spatial", selectionType)}
                            nextPage={nextRoute}
                            nextLabel="Confirm"
                            // Next IS the confirmation: it finishes the selection.
                            enableNextPage={!counts || counts.kept === 0 || savingRejection}
                            nextLoading={confirming}
                            nextPageClickFunc={() => setConfirmDialogOpen(true)}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>

            <Dialog open={confirmDialogOpen} onClose={() => !confirming && setConfirmDialogOpen(false)}>
                <DialogTitle>Confirm the repeatability result?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {counts
                            ? `${counts.kept} of ${counts.total} patches remain (${counts.total - counts.repeatable} excluded by the ΔE00 limit, ${counts.manual_rejected} excluded manually). ${continues
                                ? `The palette is reduced to these patches; ${nextStepName} follow${modeContrastType ? "s" : ""}.`
                                : "The palette is reduced to these patches and the selection is finalized."}`
                            : ""}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        disabled={confirming}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={confirmRepeatability}
                        disabled={confirming}
                        startIcon={confirming ? <CircularProgress size={16} thickness={5} /> : <CheckCircleIcon />}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                        {continues ? "Confirm" : "Confirm and finish"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
